import { create } from 'zustand';
import type { Assumptions, PackageConfig, PackageCostOverrides, PaymentTermKey, TabId, SavedScenario, MarketingSelections, PackageSelections } from '../types';
import { DEFAULT_ASSUMPTIONS, DEFAULT_PACKAGES, FEATURE_NAMES, MAX_PACKAGES, MIN_PACKAGES } from '../data/conjointData';

function clonePkg(p: PackageConfig): PackageConfig {
  return {
    ...p,
    enabledFeatures: [...p.enabledFeatures],
    costOverrides: p.costOverrides ? { ...p.costOverrides } : undefined,
  };
}

function cloneDefaultPkg(dp: typeof DEFAULT_PACKAGES[number]): PackageConfig {
  return {
    name: dp.name,
    s1OfferName: dp.s1OfferName,
    price: dp.price,
    paymentTerm: dp.paymentTerm as PaymentTermKey,
    enabledFeatures: [...dp.enabledFeatures],
    costOverrides: dp.costOverrides ? { ...dp.costOverrides } : undefined,
  };
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface InsightCacheEntry {
  hash: string;
  content: string;
}

interface SimulatorState {
  assumptions: Assumptions;
  packages: PackageConfig[];
  activeTab: TabId;
  selectedPrice: number;
  savedScenarios: SavedScenario[];

  assumptionsDrawerOpen: boolean;

  chatPanelOpen: boolean;
  chatMessages: ChatMessage[];
  lastTabBeforeChat: TabId | null;
  insightCache: Record<string, InsightCacheEntry>;
  insightExpandedTabs: Record<string, boolean>;

  setAssumption: <K extends keyof Assumptions>(key: K, value: Assumptions[K]) => void;
  setAllAssumptions: (assumptions: Partial<Assumptions>) => void;
  setFeatureCost: (featureName: string, cost: number) => void;
  setPackagePrice: (index: number, price: number) => void;
  setPackagePayment: (index: number, paymentTerm: PaymentTermKey) => void;
  toggleFeature: (packageIndex: number, featureName: string) => void;
  setAllFeaturesForPackage: (packageIndex: number, enabled: boolean) => void;
  setPackageConfig: (index: number, price: number, enabledFeatures: string[]) => void;
  setPackageCostOverride: (index: number, key: keyof PackageCostOverrides, value: number | undefined) => void;
  setPackageS1Name: (index: number, name: string) => void;
  addPackage: () => void;
  removePackage: (index: number) => void;
  setActiveTab: (tab: TabId) => void;
  setSelectedPrice: (price: number) => void;
  saveScenario: (name: string) => void;
  loadScenario: (id: string) => void;
  deleteScenario: (id: string) => void;
  resetToDefaults: () => void;

  setAssumptionsDrawerOpen: (open: boolean) => void;
  toggleAssumptionsDrawer: () => void;

  toggleChatPanel: () => void;
  setChatPanelOpen: (open: boolean) => void;
  setLastTabBeforeChat: (tab: TabId | null) => void;
  addChatMessage: (msg: ChatMessage) => void;
  updateLastAssistantMessage: (content: string) => void;
  clearChat: () => void;
  setCachedInsight: (tabId: string, hash: string, content: string) => void;
  setInsightExpanded: (tabId: string, expanded: boolean) => void;

  hydrateFromServer: (data: {
    assumptions: Assumptions;
    packages: PackageConfig[];
    selectedPrice: number;
    activeTab: TabId;
    insightCache: Record<string, InsightCacheEntry>;
    chatMessages: ChatMessage[];
    marketingSelections?: MarketingSelections;
  }) => void;
  setSavedScenarios: (scenarios: SavedScenario[]) => void;

  marketingSelections: MarketingSelections;
  setMarketingSelection: (pkgIndex: number, update: Partial<PackageSelections>) => void;
  toggleMarketingFavorite: (pkgIndex: number, taglineIndex: number) => void;
}

export const useSimulatorStore = create<SimulatorState>((set, get) => ({
  assumptions: { ...DEFAULT_ASSUMPTIONS },

  packages: DEFAULT_PACKAGES.map(cloneDefaultPkg),

  activeTab: 'summary',
  selectedPrice: 1499,
  savedScenarios: [],

  assumptionsDrawerOpen: false,
  chatPanelOpen: false,
  chatMessages: [],
  lastTabBeforeChat: null,
  insightCache: {},
  insightExpandedTabs: {},

  setAssumption: (key, value) =>
    set(state => ({ assumptions: { ...state.assumptions, [key]: value } })),

  setAllAssumptions: (partial) =>
    set(state => ({ assumptions: { ...state.assumptions, ...partial } })),

  setFeatureCost: (featureName, cost) =>
    set(state => ({
      assumptions: {
        ...state.assumptions,
        featureCosts: { ...state.assumptions.featureCosts, [featureName]: cost },
      },
    })),

  setPackagePrice: (index, price) =>
    set(state => {
      const packages = [...state.packages];
      packages[index] = { ...packages[index], price };
      return { packages };
    }),

  setPackagePayment: (index, paymentTerm) =>
    set(state => {
      const packages = [...state.packages];
      packages[index] = { ...packages[index], paymentTerm };
      return { packages };
    }),

  toggleFeature: (packageIndex, featureName) =>
    set(state => {
      const packages = [...state.packages];
      const pkg = { ...packages[packageIndex] };
      const features = [...pkg.enabledFeatures];
      const idx = features.indexOf(featureName);
      if (idx >= 0) features.splice(idx, 1);
      else features.push(featureName);
      pkg.enabledFeatures = features;
      packages[packageIndex] = pkg;
      return { packages };
    }),

  setAllFeaturesForPackage: (packageIndex, enabled) =>
    set(state => {
      const packages = [...state.packages];
      packages[packageIndex] = {
        ...packages[packageIndex],
        enabledFeatures: enabled ? [...FEATURE_NAMES] : [],
      };
      return { packages };
    }),

  setPackageConfig: (index, price, enabledFeatures) =>
    set(state => {
      const packages = [...state.packages];
      packages[index] = { ...packages[index], price, enabledFeatures: [...enabledFeatures] };
      return { packages };
    }),

  setPackageCostOverride: (index, key, value) =>
    set(state => {
      const packages = [...state.packages];
      const existing = packages[index].costOverrides || {};
      const updated = { ...existing, [key]: value };
      const cleaned: PackageCostOverrides = {};
      for (const [k, v] of Object.entries(updated)) {
        if (v !== undefined) (cleaned as Record<string, number>)[k] = v;
      }
      packages[index] = {
        ...packages[index],
        costOverrides: Object.keys(cleaned).length > 0 ? cleaned : undefined,
      };
      return { packages };
    }),

  setPackageS1Name: (index, name) =>
    set(state => {
      const packages = [...state.packages];
      packages[index] = { ...packages[index], s1OfferName: name };
      return { packages };
    }),

  addPackage: () =>
    set(state => {
      if (state.packages.length >= MAX_PACKAGES) return state;
      const letter = String.fromCharCode(65 + state.packages.length);
      const newPkg: PackageConfig = {
        name: `Package ${letter}`,
        s1OfferName: 'New S1 Offer',
        price: 999,
        paymentTerm: 'monthlyZeroApr',
        enabledFeatures: [FEATURE_NAMES[0]],
        costOverrides: { s1CostPerUnit: 55, s2CloseRate: 0.25 },
      };
      return { packages: [...state.packages, newPkg] };
    }),

  removePackage: (index) =>
    set(state => {
      if (state.packages.length <= MIN_PACKAGES) return state;
      const packages = state.packages.filter((_, i) => i !== index);
      return { packages };
    }),

  setActiveTab: (tab) => set({ activeTab: tab }),
  setSelectedPrice: (price) => set({ selectedPrice: price }),

  saveScenario: (name) => {
    const state = get();
    const scenario: SavedScenario = {
      id: `scenario-${Date.now()}`,
      name,
      timestamp: Date.now(),
      assumptions: { ...state.assumptions },
      packages: state.packages.map(clonePkg),
    };
    set(state => ({ savedScenarios: [...state.savedScenarios, scenario] }));
  },

  loadScenario: (id) => {
    const state = get();
    const scenario = state.savedScenarios.find(s => s.id === id);
    if (scenario) {
      set({
        assumptions: { ...scenario.assumptions },
        packages: scenario.packages.map(clonePkg),
      });
    }
  },

  deleteScenario: (id) =>
    set(state => ({ savedScenarios: state.savedScenarios.filter(s => s.id !== id) })),

  resetToDefaults: () =>
    set({
      assumptions: { ...DEFAULT_ASSUMPTIONS },
      packages: DEFAULT_PACKAGES.map(cloneDefaultPkg),
      insightCache: {},
      chatMessages: [],
      marketingSelections: {},
    }),

  setAssumptionsDrawerOpen: (open) => set({ assumptionsDrawerOpen: open }),
  toggleAssumptionsDrawer: () => set(state => ({ assumptionsDrawerOpen: !state.assumptionsDrawerOpen })),

  toggleChatPanel: () => set(state => ({ chatPanelOpen: !state.chatPanelOpen })),
  setChatPanelOpen: (open) => set({ chatPanelOpen: open }),
  setLastTabBeforeChat: (tab) => set({ lastTabBeforeChat: tab }),
  addChatMessage: (msg) => set(state => ({ chatMessages: [...state.chatMessages, msg] })),
  updateLastAssistantMessage: (content) => set(state => {
    const msgs = [...state.chatMessages];
    const last = msgs[msgs.length - 1];
    if (last && last.role === 'assistant') {
      msgs[msgs.length - 1] = { ...last, content };
    }
    return { chatMessages: msgs };
  }),
  clearChat: () => set({ chatMessages: [] }),
  setCachedInsight: (tabId, hash, content) => set(state => ({
    insightCache: { ...state.insightCache, [tabId]: { hash, content } },
  })),
  setInsightExpanded: (tabId, expanded) => set(state => ({
    insightExpandedTabs: { ...state.insightExpandedTabs, [tabId]: expanded },
  })),

  hydrateFromServer: (data) => set({
    assumptions: data.assumptions,
    packages: data.packages,
    selectedPrice: data.selectedPrice,
    activeTab: data.activeTab,
    insightCache: data.insightCache || {},
    chatMessages: data.chatMessages || [],
    marketingSelections: data.marketingSelections || {},
  }),

  setSavedScenarios: (scenarios) => set({ savedScenarios: scenarios }),

  marketingSelections: {},
  setMarketingSelection: (pkgIndex, update) => set(state => {
    const existing = state.marketingSelections[pkgIndex] || { favoritedTaglines: [] };
    return {
      marketingSelections: {
        ...state.marketingSelections,
        [pkgIndex]: { ...existing, ...update },
      },
    };
  }),
  toggleMarketingFavorite: (pkgIndex, taglineIndex) => set(state => {
    const existing = state.marketingSelections[pkgIndex] || { favoritedTaglines: [] };
    const favs = [...existing.favoritedTaglines];
    const idx = favs.indexOf(taglineIndex);
    if (idx >= 0) favs.splice(idx, 1);
    else favs.push(taglineIndex);
    return {
      marketingSelections: {
        ...state.marketingSelections,
        [pkgIndex]: { ...existing, favoritedTaglines: favs },
      },
    };
  }),
}));
